// The final approval gate on the wire (SEP-243 sections 36, 37, 38 and 40).
//
// THIS MODULE RUNS NO SECOND EVALUATION OF THE GATE. The gate is a
// PRECONDITION of `CreateFinalApprovedSnapshot`, so it lives where the Action
// reaches it (`vendor/septima-ontology-wealth/packages/ontology-wealth/src/final-approval-gate.ts`), and the
// ontology never depends on the contract (ADR-0003). This module holds the WIRE
// shape of the result, and the browser prints what the server computed. One
// computation, two readers.
//
// `apps/server/src/modules/onboarding/__tests__/final-approval-parity.test.ts`
// pins the two facts the ontology gate mirrors from this package: the mandatory
// half of `PROVIDED_DOCUMENT_CATALOGUE`, and the two register verdicts of
// `summariseProvidedDocuments` and `summariseReceivedDocuments`.

import { z } from 'zod';

/** One condition of section 36, as the gate judged it. */
export const finalApprovalRequirementSchema = z.object({
    code: z.string(),
    title: z.string(),
    satisfied: z.boolean(),
    /**
     * True when the persona of the reader does not admit the evidence of this
     * condition. Two sources of the gate admit a subset of the staff personas,
     * so the Desk, Ops and leadership each meet one condition they cannot see.
     * A withheld condition is NEVER satisfied, and the screen says that the
     * reader cannot see it rather than that the case fails it. A stored
     * requirement on a snapshot is never withheld, because Compliance approves
     * and Compliance admits every source.
     */
    withheld: z.boolean(),
    /** What is missing, in words. Empty when the condition is satisfied. */
    missing: z.array(z.string()),
});
export type FinalApprovalRequirementDto = z.infer<typeof finalApprovalRequirementSchema>;

/** The signature purposes of section 38. The internal sign-off is not one. */
export const mandateSignaturePurposeSchema = z.enum(['client_declaration', 'final_mandate']);
export type MandateSignaturePurpose = z.infer<typeof mandateSignaturePurposeSchema>;

export const mandateSignatureStatusSchema = z.enum([
    'skipped',
    'sent',
    'delivered',
    'completed',
    'declined',
    'voided',
]);
export type MandateSignatureStatus = z.infer<typeof mandateSignatureStatusSchema>;

/** The words the screen prints for one purpose. */
export const MANDATE_SIGNATURE_PURPOSE_LABELS: Readonly<Record<MandateSignaturePurpose, string>> = {
    client_declaration: 'Client declaration',
    final_mandate: 'Final mandate',
};

/** The words the screen prints for one status. */
export const MANDATE_SIGNATURE_STATUS_LABELS: Readonly<Record<MandateSignatureStatus, string>> = {
    skipped: 'Not requested',
    sent: 'Sent',
    delivered: 'Delivered',
    completed: 'Signed',
    declined: 'Declined',
    voided: 'Voided',
};

export const mandateSignatureRecipientSchema = z.object({
    name: z.string(),
    email: z.string(),
    recipientId: z.string(),
    status: z.string(),
});

export const mandateSignatureEnvelopeSchema = z.object({
    id: z.string(),
    snapshotId: z.string(),
    caseId: z.string(),
    clientId: z.string(),
    purpose: mandateSignaturePurposeSchema,
    provider: z.enum(['docusign', 'skipped']),
    envelopeId: z.string().nullable(),
    documentVersion: z.number().int().nullable(),
    documentName: z.string().nullable(),
    recipients: z.array(mandateSignatureRecipientSchema),
    sentAt: z.string().nullable(),
    status: mandateSignatureStatusSchema,
    statusCheckedAt: z.string().nullable(),
    signedAt: z.string().nullable(),
    signedDocumentSourceUploadId: z.string().nullable(),
    declarationCopyEntryId: z.string().nullable(),
    skipReason: z.string().nullable(),
    createdAt: z.string(),
});
export type MandateSignatureEnvelopeDto = z.infer<typeof mandateSignatureEnvelopeSchema>;

/**
 * The approved record, as a queue row and a header read it.
 *
 * The whole field block stays on the server. A reader that wants it opens the
 * audit pack, which states its provenance beside it.
 */
export const finalApprovedSnapshotSummarySchema = z.object({
    id: z.string(),
    caseId: z.string(),
    clientId: z.string(),
    caseRef: z.string().nullable(),
    prospectName: z.string().nullable(),
    assessmentId: z.string(),
    assessmentVersionId: z.string(),
    versionNumber: z.number().int(),
    clientType: z.enum(['individual', 'entity']),
    investmentManagerUserId: z.string().nullable(),
    investmentManagerName: z.string().nullable(),
    factFindVersionId: z.string().nullable(),
    vulnerabilityAssessmentId: z.string().nullable(),
    amlClearedAt: z.string().nullable(),
    requirements: z.array(finalApprovalRequirementSchema),
    approvedAt: z.string(),
    approvedByUserId: z.string().nullable(),
    approvedByName: z.string().nullable(),
    /** The optional note that Compliance wrote with the approval (SEP-332). */
    complianceNote: z.string().nullable(),
});
export type FinalApprovedSnapshotSummaryDto = z.infer<typeof finalApprovedSnapshotSummarySchema>;

/**
 * One hand-over of the final approval (SEP-332, SEP-334). The IM sends the
 * case to Compliance, and Compliance can return it to the IM with a reason.
 */
export const finalApprovalHandoverKindSchema = z.enum(['sent', 'returned']);
export type FinalApprovalHandoverKind = z.infer<typeof finalApprovalHandoverKindSchema>;

export const finalApprovalHandoverSchema = z.object({
    kind: finalApprovalHandoverKindSchema,
    /** The reason of a return. The optional note of the IM on a send. */
    note: z.string().nullable(),
    at: z.string(),
    byName: z.string().nullable(),
});
export type FinalApprovalHandoverDto = z.infer<typeof finalApprovalHandoverSchema>;

/**
 * What the case panel shows before and after the approval.
 *
 * `requirements` is the LIVE checklist while the case is unapproved, and the
 * RECORDED checklist of the snapshot once it is approved. `docusignConfigured`
 * drives the send affordance: with no key the screen offers no send, and it
 * says why (decision D6). `clientDeclaration` is the wording the client
 * declaration carries into DocuSign, which decision D4 puts behind the
 * Compliance copy seam; the signature pack prints it before the send, so the
 * sender reads what goes out.
 */
/** The two documents of the signature pack (SEP-318, decisions 4 and 6). */
export const signedDocumentKindSchema = z.enum(['mandate', 'declaration']);
export type SignedDocumentKind = z.infer<typeof signedDocumentKindSchema>;

/**
 * One retained signed copy of a document of the signature pack (SEP-343 item
 * 7). `upload` is a copy that a person uploaded. `docusign` is the document
 * that DocuSign returned.
 */
export const signedDocumentFileSchema = z.object({
    sourceUploadId: z.string(),
    document: signedDocumentKindSchema,
    filename: z.string().nullable(),
    uploadedAt: z.string().nullable(),
    byName: z.string().nullable(),
    via: z.enum(['upload', 'docusign']),
});
export type SignedDocumentFileDto = z.infer<typeof signedDocumentFileSchema>;

// ---------------------------------------------------------------------------
// The conditions of a Compliance approval (SEP-318, firm decision 2)
// ---------------------------------------------------------------------------

/** The three states of a Compliance condition. */
export const complianceConditionStatusSchema = z.enum(['open', 'resolved', 'verified']);
export type ComplianceConditionStatus = z.infer<typeof complianceConditionStatusSchema>;

/** The words the screen prints for one status. */
export const COMPLIANCE_CONDITION_STATUS_LABELS: Readonly<
    Record<ComplianceConditionStatus, string>
> = {
    open: 'Open',
    resolved: 'Resolved',
    verified: 'Verified',
};

/** The limits of the conditions of one Compliance decision. */
export const COMPLIANCE_CONDITION_MAX_COUNT = 20;
export const COMPLIANCE_CONDITION_MAX_LENGTH = 1000;

/** The text of one condition, as a request body carries it. */
export const complianceConditionTextSchema = z
    .string()
    .trim()
    .min(1)
    .max(COMPLIANCE_CONDITION_MAX_LENGTH);

/** One evidence file of the resolution of a condition. */
export const complianceConditionEvidenceSchema = z.object({
    sourceUploadId: z.string(),
    filename: z.string().nullable(),
});

/**
 * One condition that Compliance set on the approval of an Internal Review.
 * The IM records the resolution with evidence, and Compliance verifies it
 * before the final approval.
 */
export const complianceConditionSchema = z.object({
    id: z.string(),
    assessmentId: z.string(),
    complianceReviewId: z.string().nullable(),
    text: z.string(),
    status: complianceConditionStatusSchema,
    resolutionNote: z.string().nullable(),
    evidence: z.array(complianceConditionEvidenceSchema),
    resolvedAt: z.string().nullable(),
    resolvedByName: z.string().nullable(),
    verifiedAt: z.string().nullable(),
    verifiedByName: z.string().nullable(),
    /** The last reason that Compliance gave to send the condition back. */
    returnNote: z.string().nullable(),
    createdAt: z.string(),
    createdByName: z.string().nullable(),
});
export type ComplianceConditionDto = z.infer<typeof complianceConditionSchema>;

export const finalApprovalStateSchema = z.object({
    caseId: z.string(),
    clientId: z.string().nullable(),
    requirements: z.array(finalApprovalRequirementSchema),
    approvable: z.boolean(),
    snapshot: finalApprovedSnapshotSummarySchema.nullable(),
    /** The newest hand-over of the final approval, or null when none exists. */
    handover: finalApprovalHandoverSchema.nullable(),
    /**
     * The conditions of the Compliance approval of the Internal Review of the
     * case, oldest first (SEP-318). Empty when there are none, or when the
     * reader cannot see them.
     */
    complianceConditions: z.array(complianceConditionSchema),
    envelopes: z.array(mandateSignatureEnvelopeSchema),
    /** The retained signed copies of the mandate and the declaration, newest first. */
    signedDocuments: z.array(signedDocumentFileSchema),
    docusignConfigured: z.boolean(),
    /** The declaration wording the signature pack carries (decision D4). */
    clientDeclaration: z.object({
        title: z.string(),
        body: z.string(),
        entryId: z.string().nullable(),
        isPlaceholder: z.boolean(),
    }),
});
export type FinalApprovalStateDto = z.infer<typeof finalApprovalStateSchema>;

// ---------------------------------------------------------------------------
// The audit pack (SEP-243 section 40)
// ---------------------------------------------------------------------------

/**
 * One entry of the audit pack.
 *
 * The pack is a READ over the rows the earlier slices already write. It stores
 * nothing of its own, so it can never disagree with the file it describes.
 * Each entry names WHAT it is, WHEN it happened, WHO did it and WHERE the row
 * lives, so a reader can open the source.
 */
export const auditPackEntrySchema = z.object({
    /** The stable code of the entry kind. It is a value, so it is snake_case. */
    kind: z.string(),
    /** The words the pack prints for the kind. */
    kindLabel: z.string(),
    /** The Object Type key the row belongs to. */
    objectTypeKey: z.string(),
    rowId: z.string(),
    at: z.string().nullable(),
    byUserId: z.string().nullable(),
    byName: z.string().nullable(),
    summary: z.string(),
    /** The detail lines the pack prints under the summary. */
    detail: z.array(z.string()),
});
export type AuditPackEntryDto = z.infer<typeof auditPackEntrySchema>;

/** One section of the audit pack, in the order section 40 lists them. */
export const auditPackSectionSchema = z.object({
    code: z.string(),
    title: z.string(),
    entries: z.array(auditPackEntrySchema),
    /**
     * Why the section is empty, when it is. An empty section with no note means
     * the case holds nothing of that kind; a note means the platform cannot yet
     * reach it.
     */
    note: z.string().nullable(),
});
export type AuditPackSectionDto = z.infer<typeof auditPackSectionSchema>;

export const auditPackSchema = z.object({
    caseId: z.string(),
    caseRef: z.string().nullable(),
    clientId: z.string().nullable(),
    prospectName: z.string().nullable(),
    generatedAt: z.string(),
    sections: z.array(auditPackSectionSchema),
});
export type AuditPackDto = z.infer<typeof auditPackSchema>;

// ---------------------------------------------------------------------------
// The Final approvals queue (SEP-334)
// ---------------------------------------------------------------------------

/** One row of the Final approvals queue. */
export const finalApprovalQueueItemSchema = z.object({
    caseId: z.string(),
    caseRef: z.string().nullable(),
    prospectName: z.string().nullable(),
    investmentManagerName: z.string().nullable(),
    /** When the case came to Compliance, or when Compliance decided. */
    at: z.string(),
    /** Who sent the case, or who decided. */
    byName: z.string().nullable(),
    /** The note of the decision: the reason of a return or the Compliance note. */
    note: z.string().nullable(),
});
export type FinalApprovalQueueItemDto = z.infer<typeof finalApprovalQueueItemSchema>;

export const finalApprovalDecisionOutcomeSchema = z.enum(['approved', 'returned']);

export const finalApprovalQueueSchema = z.object({
    /** Cases that the IM sent and that Compliance has not decided. */
    waiting: z.array(finalApprovalQueueItemSchema),
    /** Cases that Compliance approved or returned, newest first. */
    decided: z.array(
        finalApprovalQueueItemSchema.extend({ outcome: finalApprovalDecisionOutcomeSchema }),
    ),
});
export type FinalApprovalQueueDto = z.infer<typeof finalApprovalQueueSchema>;

// ---------------------------------------------------------------------------
// The case trail (SEP-338)
// ---------------------------------------------------------------------------

/**
 * One event of the case audit trail that is not a stage move: the AML check,
 * the peer and Compliance decisions, the document steps and the final approval.
 * The server reads each one from the row that holds it, so the trail cannot
 * disagree with the file.
 */
export const caseTrailEventSchema = z.object({
    id: z.string(),
    /** The stable code of the kind. It is a value, so it is snake_case. */
    kind: z.string(),
    kindLabel: z.string(),
    summary: z.string(),
    detail: z.array(z.string()),
    at: z.string().nullable(),
    byName: z.string().nullable(),
});
export type CaseTrailEventDto = z.infer<typeof caseTrailEventSchema>;
export const caseTrailSchema = z.object({
    caseId: z.string(),
    events: z.array(caseTrailEventSchema),
});
export type CaseTrailDto = z.infer<typeof caseTrailSchema>;
